import type { IListUserOption } from "@project/emerge-types";
import find from "lodash/find";

export type OptionDBType<TOption extends IListUserOption = IListUserOption> = {
  [optionKey: string]: TOption[];
};

export class OptionDB<TOption extends IListUserOption = IListUserOption> {
  private errors: string[] = [];
  constructor(private readonly options: OptionDBType<TOption>) {
    this.options = options;
  }
  public resetErrors() {
    this.errors = [];
  }
  public getErrors(): string[] {
    return this.errors;
  }
  public setOption(optionKey: string, option: TOption) {
    this.options[optionKey] ??= [];
    this.options[optionKey].push(option);
  }
  public getAnyByName(name: string): TOption | undefined {
    const allOptions = Object.values(this.options).flat();
    return find(
      allOptions,
      (option) =>
        (option.name &&
          option.name.replace(/\s/g, "") === name.replace(/\s/g, "")) ||
        (option.aliases && option.aliases.indexOf(name) !== -1),
    ) as TOption | undefined;
  }
  public getByName<T extends TOption = TOption>(key: string, name: string): T {
    if (!this.options[key]) {
      throw new Error(`unknown option type ${key}`);
    }
    if (name === "default") {
      return this.options[key][0] as T;
    }
    const result = find(
      this.options[key],
      (option) =>
        (option.name &&
          option.name.replace(/\s/g, "") === name.replace(/\s/g, "")) ||
        (option.aliases && option.aliases.indexOf(name) !== -1),
    );

    if (result === undefined) {
      this.errors.push(
        `unknown "${key}" "${name}" replaced by ${this.options[key][0]?.name}`,
      );
      return this.options[key][0] as T;
    } else {
      return result as T;
    }
  }
  /**
   * Matches a filter against an option, resolving `name` the same way
   * {@link getByName} does: whitespace-insensitive, and satisfied by an alias.
   * Options are renamed over time (M1414 → PLW1415) and keep the old name as an
   * alias, so a plain `find` on `name` misses call sites that were not renamed.
   */
  private matchesFilter<T extends TOption = TOption>(
    option: TOption,
    filter: Partial<T>,
  ): boolean {
    return Object.entries(filter).every(([field, expected]) => {
      const actual = (option as Record<string, unknown>)[field];
      if (field !== "name" || typeof expected !== "string") {
        return actual === expected;
      }
      const nameMatches =
        typeof actual === "string" &&
        actual.replaceAll(/\s/g, "") === expected.replaceAll(/\s/g, "");
      return nameMatches || (option.aliases?.indexOf(expected) ?? -1) !== -1;
    });
  }
  public getOrThrow<T extends TOption = TOption>(
    key: string,
    filter: Partial<T>,
  ): T {
    if (filter.name === "default") {
      if (!this.options[key]) {
        throw new Error(`unknown DB item" ${key}: ${JSON.stringify(filter)}`);
      }
      return this.options[key][0] as T;
    }
    const result = find(this.options[key], (option) =>
      this.matchesFilter(option, filter),
    );

    if (result === undefined) {
      throw new Error(`unknown DB item" ${key}: ${JSON.stringify(filter)}`);
    } else {
      return result as T;
    }
  }
  public get<T extends TOption = TOption>(
    key: string,
    filter: Partial<T>,
  ): T | undefined {
    if (filter.name === "default") {
      if (!this.options[key]) {
        return undefined;
      }
      return this.options[key][0] as T;
    }
    const result = find(this.options[key], (option) =>
      this.matchesFilter(option, filter),
    );
    return result as T | undefined;
  }
  public getOptions<T extends TOption = TOption>(key: string): T[] {
    return this.options[key] as T[];
  }
  public getAllOfKind<T extends TOption = TOption>(kind: string): T[] {
    return this.options[kind] as T[];
  }
}
